import { FileCheck, XCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { cn } from "@/lib/utils";

type SonnerSize = "sm" | "md" | "lg";
type SonnerVariant = "default" | "error";

interface CustomToastProps {
    title: string;
    description?: string;
    variant?: SonnerVariant;
    size?: SonnerSize;
    showIcon?: boolean;
    CustomIcon?: React.ComponentType<{ className?: string }>;
}

interface CustomToasterNotifyProps {
    title: string;
    description: string;
    CustomIcon?: React.ComponentType<{ className?: string }>;
}

const sizeClasses: Record<SonnerSize, string> = {
    sm: "w-72 p-3 gap-2",
    md: "w-80 p-3.5 gap-2.5",
    lg: "w-96 p-4 gap-3",
};

const baseContainerClasses = "bg-background-general-card border-border-default";

const textClasses: Record<SonnerVariant, { title: string; description: string }> = {
    default: {
        title: "text-text-default",
        description: "text-text-subtlest",
    },
    error: {
        title: "text-destructive font-semibold",
        description: "text-destructive",
    },
};

const iconClasses: Record<SonnerVariant, string> = {
    default: "text-semantic-icon-high",
    error: "text-destructive",
};

const defaultIcons: Record<SonnerVariant, React.ComponentType<{ className?: string }>> = {
    default: FileCheck,
    error: XCircle,
};

export const SonnerProvider = () => <Toaster position="top-right" richColors theme="dark" />;

export const CustomToast = ({
    title,
    description,
    variant = "default",
    size = "md",
    showIcon = true,
    CustomIcon,
}: CustomToastProps) => {
    const Icon = CustomIcon ?? defaultIcons[variant];
    return (
        <div
            className={cn(
                "flex flex-row items-center rounded-[10px] border shadow-md",
                sizeClasses[size],
                baseContainerClasses
            )}
        >
            {showIcon && (
                <div className="flex-shrink-0">
                    <Icon className={cn("size-5", iconClasses[variant])} />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <p className={cn("text-sm font-medium leading-5", textClasses[variant].title)}>{title}</p>
                {description && (
                    <p className={cn("text-sm leading-5 mt-0.5", textClasses[variant].description)}>{description}</p>
                )}
            </div>
        </div>
    );
};

export const notify = {
    error: ({ title, description, CustomIcon }: CustomToasterNotifyProps) =>
        toast.custom(() => (
            <CustomToast title={title} description={description} variant="error" CustomIcon={CustomIcon} />
        )),

    info: ({ title, description, CustomIcon }: CustomToasterNotifyProps) =>
        toast.custom(() => (
            <CustomToast title={title} description={description} variant="default" CustomIcon={CustomIcon} />
        )),

    success: ({ title, description, CustomIcon }: CustomToasterNotifyProps) =>
        toast.custom(() => (
            <CustomToast title={title} description={description} variant="default" CustomIcon={CustomIcon} />
        )),
};
